import React, { useState, useRef, useEffect } from 'react'
import { Menu, X, Globe, Search, Check } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { LANGUAGES } from '@/data/languages'
import { useT } from '@/context/TranslateCtx'

const LINKS = [
  { k: 'nav.features', href: '#features' },
  { k: 'nav.pricing', href: '#pricing' },
  { k: 'nav.security', href: '#security' },
  { k: 'nav.docs', href: '#docs' },
  { k: 'nav.contact', href: '#contact' },
]

const flag = c => `/flags/${c}.svg`
const menuAnim = { hidden: { y: -200, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 320, damping: 24 } }, exit: { y: -200, opacity: 0, transition: { duration: 0.2 } } }

function LanguagePicker({ lang, onChange }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const btnRef = useRef(null)
  const panelRef = useRef(null)

  useEffect(() => {
    const h = e => {
      if (!open) return
      if (panelRef.current && !panelRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false)
    }
    window.addEventListener('mousedown', h)
    return () => window.removeEventListener('mousedown', h)
  }, [open])

  const current = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0]
  const filtered = query === ''
    ? LANGUAGES
    : LANGUAGES.filter(({ label, code, name = '' }) => {
        const q = query.toLowerCase()
        return (
          label.toLowerCase().includes(q) ||
          code.toLowerCase().includes(q) ||
          name.toLowerCase().includes(q)
        )
      })

  const pick = code => { onChange(code); setOpen(false); setQuery('') }

  return (
    <div className="relative">
      <button ref={btnRef} onClick={() => setOpen(o => !o)} className="flex items-center gap-2 px-4 py-2 border rounded-full bg-white hover:bg-gray-50 text-base md:text-lg">
        <img src={flag(current.flag)} alt="" className="h-5 w-5" />
        {/* <span className="truncate">{current.label}</span> */}
        <Globe size={18} className="ml-1 text-gray-500" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div ref={panelRef} initial={{ opacity: 0, scale: 0.95, y: -4 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: -4 }} transition={{ type: 'spring', stiffness: 260, damping: 22 }} className="absolute right-0 mt-2 w-64 max-h-72 bg-white border shadow-xl rounded-xl overflow-hidden z-[60]">
            <div className="p-3 border-b bg-white sticky top-0">
              <label className="relative block">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search…" className="w-full pl-9 pr-3 py-2 border rounded-md text-base focus:outline-none focus:ring-1 focus:ring-primary" />
              </label>
            </div>
            <ul className="max-h-52 overflow-auto">
              {filtered.length === 0 && <li className="px-4 py-4 text-center text-gray-500 text-base">No results</li>}
              {filtered.map(item => (
                <li key={item.code} onClick={() => pick(item.code)} className={`flex items-center gap-3 px-4 py-2 cursor-pointer text-base hover:bg-primary/10 ${item.code === lang ? 'bg-primary/10' : ''}`}>
                  <img src={flag(item.flag)} alt="" className="h-5 w-5" />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.code === lang && <Check size={18} className="text-primary" />}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function NavBar() {
  const [open, setOpen] = useState(false)
  const { t, ready, lang, changeLang } = useT()

  return (
    <>
      {!ready && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-white/70 backdrop-blur">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <header className="fixed top-0 left-0 w-full z-50 backdrop-blur-md bg-white/80 shadow-md">
        <nav className="container flex items-center justify-between h-20">
          <a href="/" className="flex items-center text-2xl md:text-3xl font-extrabold text-primary">
            <img src="/assets/favicon.png" alt="logo" className="h-9 mr-2" />  
          </a>
          <ul className="hidden md:flex items-center gap-8">
            {LINKS.map(l => (
              <li key={l.href}>
                <a href={l.href} className="text-lg font-medium text-gray-700 hover:text-primary">
                  {ready ? t(l.k) : l.k.split('.').pop()}
                </a>
              </li>
            ))}
            <li><LanguagePicker lang={lang} onChange={changeLang} /></li>
            <li>
              <a href="https://app.kuizy.ai/login" className="px-5 py-2 text-lg font-semibold rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50">
                {ready ? t('nav.login') : 'Log in'}
              </a>
            </li>
            <li>
              <a href="https://app.kuizy.ai/register" className="px-6 py-2 text-lg font-semibold rounded-full bg-primary text-white hover:bg-secondary">
                {ready ? t('nav.start') : 'Get started'}
              </a>
            </li>
          </ul>
          <button onClick={() => setOpen(!open)} className="md:hidden text-gray-700">{open ? <X size={32} /> : <Menu size={32} />}</button>
        </nav>
        <AnimatePresence>
          {open && (
            <motion.div className="md:hidden absolute top-20 left-0 w-full bg-white shadow-lg" variants={menuAnim} initial="hidden" animate="visible" exit="exit">
              <ul className="flex flex-col gap-6 p-6">
                {LINKS.map(l => (
                  <motion.li key={l.href} whileHover={{ x: 10 }}>
                    <a href={l.href} onClick={() => setOpen(false)} className="block text-lg font-medium text-gray-700 hover:text-primary">
                      {ready ? t(l.k) : l.k.split('.').pop()}
                    </a>
                  </motion.li>
                ))}
                <li className="border-t pt-4">
                  <LanguagePicker lang={lang} onChange={code => { changeLang(code); setOpen(false) }} />
                </li>
                <motion.li whileHover={{ scale: 1.02 }}>
                  <a href="https://app.kuizy.ai/login" onClick={() => setOpen(false)} className="block w-full text-center px-6 py-3 text-lg font-semibold rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50">
                    {ready ? t('nav.login') : 'Log in'}
                  </a>
                </motion.li>
                <motion.li whileHover={{ scale: 1.05 }}>
                  <a href="https://app.kuizy.ai/register" onClick={() => setOpen(false)} className="block w-full text-center mt-2 px-6 py-3 text-lg font-semibold rounded-full bg-primary text-white hover:bg-secondary">
                    {ready ? t('nav.start') : 'Get started'}
                  </a>
                </motion.li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  )
}
